import { useIsDemo } from "@/demo/DemoContext";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CreateCustomerDialog } from "@/components/CreateCustomerDialog";
import { CreateProductDialog } from "@/components/CreateProductDialog";
import { useArOnboardingStatus } from "@/hooks/useArOnboardingStatus";
import { useArStats } from "@/hooks/useArStats";
import { useArActivity } from "@/hooks/useArActivity";
import { useChat } from "@/components/chat/ChatProvider";
import type { components } from "@dolfin/api-admin";
import { Kbd } from "@/components/ui/kbd";
import { CheckIcon, ChevronRight, Plus } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { cn } from "@/lib/utils";
import { PageContainer, PageHeader, StatCard } from "@/components/PagePrimitives";
import { LoadingState } from "@/components/PageUi";
import { OnboardingSteps } from "./ApOnboardingPage";
import { RowPanel, type PanelRow } from "@/components/RowPanel";

const steps = [
  {
    key: "paymentsEnabled" as const,
    title: "Set up payments",
    description: "Connect Stripe so customers can pay your invoices instantly by card.",
  },
  {
    key: "customerCreated" as const,
    title: "Create a customer",
    description: "Every invoice needs a customer. Add one to get started.",
  },
  {
    key: "productCreated" as const,
    title: "Add a product",
    description: "Define what you're billing for - name, price, currency.",
  },
  {
    key: "invoiceCreated" as const,
    title: "Create and send an invoice",
    description: "Combine your customer and product. The agent handles delivery.",
  },
];

export function ArOnboardingPage() {
  const isDemo = useIsDemo();
  const navigate = useNavigate();
  const { activeOrganisationId } = useAuth();
  const status = useArOnboardingStatus();
  const patchedRef = useRef(false);

  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  useEffect(() => {
    if (
      status.completedCount === 4 &&
      !status.hasBeenOnboarded &&
      !patchedRef.current &&
      activeOrganisationId &&
      !isDemo
    ) {
      patchedRef.current = true;
      api.PATCH("/v1/organisations/{id}", {
        params: { path: { id: activeOrganisationId } },
        body: { hasBeenOnboarded: true },
      }).then(() => status.refresh());
    }
  }, [status.completedCount, status.hasBeenOnboarded, activeOrganisationId, isDemo]);

  function handleStepClick(index: number) {
    switch (index) {
      case 0:
        navigate("/settings/payments");
        break;
      case 1:
        setCustomerDialogOpen(true);
        break;
      case 2:
        setProductDialogOpen(true);
        break;
      case 3:
        navigate("/invoices/new");
        break;
    }
  }

  // A demo arrives fully seeded: it never sees the checklist.
  if (isDemo) {
    return <ArOverview onNewInvoice={() => navigate("/invoices/new")} />;
  }

  if (status.loading) {
    return (
      <PageContainer>
        <LoadingState label="Loading accounts receivable..." />
      </PageContainer>
    );
  }

  if (status.hasBeenOnboarded) {
    return <ArOverview onNewInvoice={() => navigate("/invoices/new")} />;
  }

  return (
    <OnboardingSteps
      badge="AR sandbox"
      title="Accounts receivable"
      intro={
        <>
          No real data, no real payments. Follow the steps or ask the assistant with <Kbd>&#8984;I</Kbd>
        </>
      }
      steps={steps.map((step) => ({ ...step, complete: status.steps[step.key] }))}
      onStep={handleStepClick}
    >
      <CreateCustomerDialog
        open={customerDialogOpen}
        onOpenChange={setCustomerDialogOpen}
        onCreated={() => status.refresh()}
      />
      <CreateProductDialog
        open={productDialogOpen}
        onOpenChange={setProductDialogOpen}
        onCreated={() => status.refresh()}
      />
    </OnboardingSteps>
  );
}

type CurrencyTotal = components["schemas"]["CurrencyTotal"];
type InvoiceActivity = components["schemas"]["InvoiceActivityResponse"];

const num = (v: number | string | null | undefined): number =>
  typeof v === "number" ? v : v == null ? 0 : Number(v) || 0;

function relativeTime(iso: string | undefined): string {
  if (!iso) return "";
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

function stateLabel(state: InvoiceActivity["toState"]): string {
  return state === "PartiallyPaid" ? "Partially paid" : String(state ?? "");
}

// Returned as parts so the customer renders as its own link between the label and the amount.
interface ActivityDescription {
  label: string;
  showCustomer: boolean;
  amount?: string;
}

function activityDescription(a: InvoiceActivity): ActivityDescription {
  switch (a.action) {
    case "InvoiceCreated":
      return { label: "Drafted", showCustomer: false };
    case "InvoiceStateChanged":
      return {
        label: stateLabel(a.toState),
        showCustomer: true,
        amount: isPaymentRow(a) ? money(a.currency, a.totalAmount) : undefined,
      };
    case "InvoiceSent":
      return { label: "Sent", showCustomer: true };
    case "CreditNoteIssued":
      return { label: "Credit note issued", showCustomer: true };
    case "InvoiceReminderSent":
      return { label: "Reminder sent", showCustomer: true };
    case "InvoiceReminderFailed":
      return { label: "Reminder failed", showCustomer: true };
    default:
      return { label: "", showCustomer: false };
  }
}

// Paid / partially-paid rows show the amount; everything else shows relative time.
function isPaymentRow(a: InvoiceActivity): boolean {
  return (
    a.action === "InvoiceStateChanged" &&
    (a.toState === "Paid" || a.toState === "PartiallyPaid")
  );
}

function money(currency: string | undefined, amount: number | string | null | undefined): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "GBP",
      maximumFractionDigits: 0,
    }).format(num(amount));
  } catch {
    return `${Math.round(num(amount))}`;
  }
}

function primaryCurrency(list?: CurrencyTotal[]): CurrencyTotal | null {
  return list && list.length > 0 ? list[0]! : null;
}

function ArOverview({ onNewInvoice }: { onNewInvoice: () => void }) {
  const stats = useArStats();
  const activity = useArActivity(5);
  const { openChat, sendMessage } = useChat();

  const askAgent = (prompt: string) => {
    openChat();
    void sendMessage(prompt);
  };

  const outstandingPrimary = primaryCurrency(stats.outstanding?.totals.outstandingByCurrency);
  const overduePrimary = primaryCurrency(stats.overdue?.totals.overdueByCurrency);
  const received = stats.received30;
  const deltaPct = received?.trend.deltaPercent != null ? num(received.trend.deltaPercent) : null;
  const receivedSub =
    deltaPct === null
      ? received
        ? "vs £0 prior"
        : undefined
      : `${deltaPct >= 0 ? "+" : ""}${Math.round(deltaPct * 100)}% vs prior 30d`;
  const receivedTrend =
    received?.trend.direction === "up" ? "up" : received?.trend.direction === "down" ? "down" : undefined;

  const dash = stats.loading ? "..." : "-";

  // The headline is the primary currency only; other currencies are in a tooltip.
  const receivedCurrencies = received?.receivedByCurrency ?? [];
  const hasOtherCurrencies = receivedCurrencies.some((c) => c.currency !== received?.currency);
  const receivedValue = !received ? (
    dash
  ) : !hasOtherCurrencies ? (
    money(received.currency, received.summary.totalReceived)
  ) : (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-4">
          {money(received.currency, received.summary.totalReceived)}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-0.5">
          {receivedCurrencies.map((c) => (
            <div key={c.currency} className="flex items-center justify-between gap-4 tabular-nums">
              <span>{money(c.currency, c.amount)}</span>
              <span className="opacity-70">
                {num(c.count)} payment{num(c.count) === 1 ? "" : "s"}
              </span>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );

  const navigate = useNavigate();
  const openInvoice = (id?: string) => (id ? () => navigate(`/invoices/${id}`) : undefined);

  const overdueCustomers = stats.overdue?.customers ?? [];
  const attentionRows: PanelRow[] = overdueCustomers.slice(0, 4).map((c, i) => ({
    key: c.customerId ?? `overdue-${i}`,
    primary: c.customerName,
    secondary: `${c.oldestInvoice?.invoiceNumber ?? "Invoice"} is the oldest unpaid`,
    right: `${num(c.oldestInvoice?.daysOverdue)} days late`,
    rightTone: "red",
    onOpen: openInvoice(c.oldestInvoice?.invoiceId),
  }));

  const topCustomers = stats.received90?.topCustomers ?? [];
  const topCustomerRows: PanelRow[] = topCustomers.slice(0, 5).map((c, i) => ({
    key: c.customerId ?? `top-${i}`,
    primary: c.customerName,
    secondary: `${num(c.paymentCount)} payment${num(c.paymentCount) === 1 ? "" : "s"}`,
    right: money(stats.received90?.currency, c.received),
    onOpen: c.customerId ? () => navigate(`/customers/${c.customerId}`) : undefined,
  }));

  const activityRows: PanelRow[] = activity.items.map((a, i) => {
    const desc = activityDescription(a);
    return {
      key: a.id ?? `activity-${i}`,
      primary: (
        <>
          {a.documentNumber}
          {desc.label ? <span className="font-normal text-muted-foreground"> · {desc.label}</span> : null}
        </>
      ),
      secondary: [desc.showCustomer ? a.customerName : null, relativeTime(a.createdAt)].filter(Boolean).join(" · "),
      right: desc.amount,
      rightTone: desc.amount ? "green" : undefined,
      onOpen: openInvoice(a.invoiceId),
    };
  });

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Receivables"
        title="Overview"
        actions={
          <Button onClick={onNewInvoice} className="gap-1.5">
            <Plus className="size-4" />
            New invoice
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-3">
        <StatCard
          label="Outstanding"
          value={outstandingPrimary ? money(outstandingPrimary.currency, outstandingPrimary.amount) : dash}
          sub={
            stats.outstanding ? `${stats.outstanding.totals.invoiceCount} invoices` : undefined
          }
          onAskAgent={() => askAgent("Show me my outstanding invoices and what I should do.")}
        />
        <StatCard
          label="Overdue"
          value={overduePrimary ? money(overduePrimary.currency, overduePrimary.amount) : stats.overdue ? money(outstandingPrimary?.currency, 0) : dash}
          sub={stats.overdue ? `${stats.overdue.totals.customerCount} customers` : undefined}
          trend={overduePrimary ? "down" : undefined}
          onAskAgent={() => askAgent("Show me my overdue invoices and recommend what to do about each one.")}
        />
        <StatCard
          label="Received · 30d"
          value={receivedValue}
          sub={receivedSub}
          trend={receivedTrend}
          onAskAgent={() => askAgent("Summarise what I've collected over the last 30 days and how I'm trending.")}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 @xl:grid-cols-2">
        <RowPanel title="Needs attention" rows={attentionRows} loading={stats.loading} empty="Nothing overdue." />
        <RowPanel title="Recent activity" rows={activityRows} loading={activity.loading} empty="No activity yet." />
      </div>

      <div className="mt-5">
        <RowPanel
          title="Top customers · last 90 days"
          rows={topCustomerRows}
          loading={stats.loading}
          empty="No payments in the last 90 days."
        />
      </div>
    </PageContainer>
  );
}
